import React from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faRefresh } from '@fortawesome/free-solid-svg-icons';
import ServerTable from '../../Components/Table/ServerTabe';
import { ReportsApi } from '../../Services/ConciliationApiDataService';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';
import { Permissions } from '../../Security/permissions';
import { useSecurity } from '../../Contexts/SecurityContext';

/* utility */
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
};

const useToken = () => {
  const { instance, accounts } = useMsal();
  return React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);
};

const UnmatchedInvoicesReportContent = () => {
  const getToken = useToken();
  const { companiesFor, hasCompanyPermission } = useSecurity();

  // company selector
  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);

  // toolbar refresh tick
  const [refreshTick, setRefreshTick] = React.useState(0);

  // filters
  const [search, setSearch] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [vendorCode, setVendorCode] = React.useState('');
  const [nit, setNit] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');

  // preload companies
  React.useEffect(() => {
    const opts = companiesFor(Permissions.Conciliation.Reports).map(company => ({
      value: company.companyId,
      label: company.companyName || company.companyDb || `Company ${company.companyId}`
    }));
    setCompanyOptions(opts);
    setLoadingCompanies(false);
    if (companyId && !opts.some(option => option.value === companyId)) {
      setCompanyId(null);
      setStoredConciliationCompanyId(null);
    }
  }, [companiesFor, companyId]);

  const selectedCompany = React.useMemo(
    () => companyOptions.find(o => o.value === companyId) ?? null,
    [companyOptions, companyId]
  );
  const authorizedCompanyId = companyId && hasCompanyPermission(companyId, Permissions.Conciliation.Reports)
    ? companyId
    : null;

  // server-side data fetch for table
  const fetchData = React.useCallback(async ({ pageIndex, pageSize }) => {
    if (!authorizedCompanyId) return { items: [], totalItems: 0 };
    const token = await getToken();

    const res = await ReportsApi.getUnmatchedInvoices(
      authorizedCompanyId,
      {
        page: pageIndex + 1,
        pageSize,
        search: (search || '').trim(),
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        vendorCode: vendorCode || null,
        nit: nit || null,
        minAmount: minAmount ? Number(minAmount) : null,
        maxAmount: maxAmount ? Number(maxAmount) : null
      },
      token
    );

    return {
      items: res.items ?? [],
      totalItems: res.totalItems ?? 0
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedCompanyId, getToken, refreshTick, search, dateFrom, dateTo, vendorCode, nit, minAmount, maxAmount]);

  // export helpers
  const exportFile = async (format) => {
    if (!authorizedCompanyId) return;
    try {
      const token = await getToken();
      const blob = await ReportsApi.exportUnmatchedInvoices(
        authorizedCompanyId,
        {
          search: (search || '').trim(),
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
          vendorCode: vendorCode || null,
          nit: nit || null,
          minAmount: minAmount ? Number(minAmount) : null,
          maxAmount: maxAmount ? Number(maxAmount) : null
        },
        format,
        token
      );
      const filename = `unmatched_invoices_${authorizedCompanyId}_${new Date().toISOString().slice(0,10)}.${format}`;
      saveBlob(blob, filename);
    } catch (e) {
      console.error('Export failed', e);
      alert('Export failed. Please try again.');
    }
  };

  // columns — align with the view/DTO you exposed for screen #4
  const columns = React.useMemo(() => [
    { header: 'InvoiceId', accessorKey: 'sourceInvoiceId' },
    { header: 'Vendor Code', accessorKey: 'vendorCode' },
    { header: 'Vendor Name', accessorKey: 'vendorName' },
    { header: 'NIT', accessorKey: 'vendorNit' },
    { header: 'Series', accessorKey: 'invoiceSeries' },
    { header: 'Number', accessorKey: 'invoiceNumber' },
    { 
        header: 'Doc Date', 
        accessorKey: 'docDate', 
        cell: info => formatGuatemalanDateTime(info.getValue()),
        meta: {
            filterVariant: 'date-range',
        },
        filterFn: 'dateBetweenFilterFn' 
    },
    { header: 'Currency', accessorKey: 'currency' },
    { header: 'Amount', accessorKey: 'amount', cell: info => info.getValue() != null ? Number(info.getValue()).toLocaleString() : '', meta: { filterVariant: 'range' } },
    { header: 'Reason', accessorKey: 'reasonUnmatched' },
    { 
        header: 'First Seen', 
        accessorKey: 'firstSeenOn',
        cell: info => formatGuatemalanDateTime(info.getValue()),
        meta: {
            filterVariant: 'date-range',
        },
        filterFn: 'dateBetweenFilterFn'
     },
    { 
        header: 'Last Checked', 
        accessorKey: 'lastCheckedOn',
        cell: info => formatGuatemalanDateTime(info.getValue()),
        meta: {
            filterVariant: 'date-range',
        },
        filterFn: 'dateBetweenFilterFn'
     },
  ], []);

  return (
    <Container fluid className="h-100 d-flex flex-column">
      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-end w-100 mb-3">
        <div style={{ minWidth: 280 }}>
          <label className="form-label mb-1">Company</label>
          <Select
            classNamePrefix="react-select"
            options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
            value={selectedCompany}
            onChange={(opt) => {
              const id = opt?.value ?? null;
              setCompanyId(id);
              setStoredConciliationCompanyId(id);
            }}
            isLoading={loadingCompanies}
            placeholder="Select company…"
            menuPortalTarget={document.body}
            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
          />
        </div>

        <div className="d-flex align-items-end" style={{ gap: 8 }}>
          <button
            className="btn btn-outline-primary fw-bold"
            onClick={() => setRefreshTick(t => t + 1)}
            disabled={!authorizedCompanyId}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
          <button
            className="btn btn-primary fw-bold"
            onClick={() => exportFile('csv')}
            disabled={!authorizedCompanyId}
            title="Export CSV"
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            CSV
          </button>
          <button
            className="btn btn-primary fw-bold"
            onClick={() => exportFile('xlsx')}
            disabled={!authorizedCompanyId}
            title="Export Excel"
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Excel
          </button>
        </div>
      </div>

      {/* Filters row */}
      {/* <Row className="g-3 mb-2">
        <Col sm={12} md={4}>
          <label className="form-label">Search (free text)</label>
          <input
            className="form-control"
            placeholder="Vendor, NIT, invoice number, etc."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">Date From</label>
          <input type="date" className="form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">Date To</label>
          <input type="date" className="form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">Min Amount</label>
          <input type="number" className="form-control" value={minAmount} onChange={e => setMinAmount(e.target.value)} />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">Max Amount</label>
          <input type="number" className="form-control" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">Vendor Code</label>
          <input className="form-control" value={vendorCode} onChange={e => setVendorCode(e.target.value)} />
        </Col>
        <Col sm={6} md={2}>
          <label className="form-label">NIT</label>
          <input className="form-control" value={nit} onChange={e => setNit(e.target.value)} />
        </Col>
      </Row> */}

      {/* Table */}
      <Row className="my-2">
        <Col sm={12}>
          {!authorizedCompanyId && (
            <div className="alert alert-info mb-3">
              Select a company to load unmatched-invoice records.
            </div>
          )}
          <ServerTable
            columns={columns}
            fetchData={fetchData}
            tableClass="table-hover "
            containerClass="table-responsive"
            headerClass="table-light fit-header"
            enableGlobalFilter={false}
            initialPageSize={20}
          />
        </Col>
      </Row>
    </Container>
  );
};

const UnmatchedInvoicesReportPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Unmatched Invoices (No Payment Found)">
      <UnmatchedInvoicesReportContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default UnmatchedInvoicesReportPage;
